import { useAuthStore } from '../stores/auth.store.ts';
import { formatCurrency, formatBs, convertToBs } from './currency.ts';
import { formatBirthdayInfo, formatClientTenure } from './crmHelpers.ts';
import { inferTemplateCategoryFromRules } from './segmentRuleInference.ts';
import { interpolateTemplate } from './templateEngine.ts';
import type { CrmClient, CrmSegment } from '@/types/crm.ts';
import type { Settings } from '@/types/settings.ts';

/**
 * Get effective business name from settings override or auth store (Fresha provider) or default.
 */
export function getEffectiveBusinessName(settings: Settings): string {
  if (settings.businessName && settings.businessName.trim()) {
    return settings.businessName.trim();
  }
  const authState = useAuthStore.getState();
  return authState.freshaProvider || authState.provider?.data?.attributes?.name || 'Tu Centro / Negocio';
}

/**
 * Format client display name respecting mode (first_name, full_name, or alias).
 */
export function getClientDisplayName(
  client: CrmClient,
  mode: 'first_name' | 'full_name' | 'alias' = 'first_name',
  alias = ''
): string {
  if (mode === 'alias' && alias) return alias;
  if (mode === 'full_name') return client.name || 'Cliente';

  if (client.firstName) return client.firstName;
  const parts = (client.name || '').trim().split(/\s+/);
  return parts[0] || 'Cliente';
}

/**
 * Generate contextual greeting based on current time.
 */
export function getContextualGreeting(mode: 'dynamic' | 'plain' = 'dynamic', prefix = 'Hola'): string {
  if (mode === 'plain') return prefix;
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'Buenos días';
  if (hour >= 12 && hour < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

import { loadRatesCache } from '../services/rates.service.ts';

/**
 * Build dictionary of all available template tags for a CRM client.
 */
export function buildCrmTags(
  client: CrmClient,
  settings: Settings,
  rateOverride?: number
): Record<string, string> {
  const nameMode: 'first_name' | 'full_name' | 'alias' =
    settings.customerNameMode === 'alias'
      ? 'alias'
      : settings.nameMode === 'full'
        ? 'full_name'
        : 'first_name';

  const cliente = getClientDisplayName(client, nameMode, settings.customerAlias);
  const primerNombre = client.firstName || (client.name || '').trim().split(/\s+/)[0] || 'Cliente';
  const nombreCompleto = client.name || primerNombre;
  const parts = (client.name || '').trim().split(/\s+/);
  const apellido = client.lastName || (parts.length > 1 ? parts.slice(1).join(' ') : '');

  const saludo = getContextualGreeting(settings.greetingMode, settings.greetingPlainPrefix);
  const negocio = getEffectiveBusinessName(settings);
  const profesional = client.lastStaffMember || 'Nuestro equipo';
  const dias = String(client.daysAbsent ?? 0);
  const citas = String(client.totalAppointments ?? 0);
  const consumoNum = client.totalSales ?? 0;
  const consumo = formatCurrency(consumoNum, settings.currencyCode || 'USD');
  const deudaNum = client.outstandingAmount ?? 0;
  const deuda = formatCurrency(deudaNum, settings.currencyCode || 'USD');
  const ultimaCita = client.lastAppointmentDate || 'recientemente';
  const ultimoServicio = client.lastServiceName || 'tu servicio';
  const ultimaSede = client.lastLocationName || 'nuestro centro';
  const firma = settings.closingText || '';

  const cachedRates = loadRatesCache();
  const autoRate = cachedRates.latest[settings.currencyCode || 'USD']?.rate || 0;
  const activeRate = rateOverride !== undefined && rateOverride > 0
    ? rateOverride
    : (settings.manualRate || autoRate || 0);

  const deudaBs = activeRate > 0 && deudaNum > 0
    ? formatBs(convertToBs(deudaNum, activeRate, settings.recargo))
    : '';
  const consumoBs = activeRate > 0 && consumoNum > 0
    ? formatBs(convertToBs(consumoNum, activeRate, settings.recargo))
    : '';

  // Birthday derivation
  const bdayInfo = formatBirthdayInfo(client.birthdayDay, client.birthdayMonth);
  const monthNames = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  const cumpleanos = client.birthdayDay && client.birthdayMonth
    ? `${client.birthdayDay} de ${monthNames[(client.birthdayMonth || 1) - 1]}`
    : 'próximamente';
  const diasCumpleanos = bdayInfo?.isBirthdayToday
    ? 'hoy'
    : bdayInfo?.daysUntil === 1
      ? 'mañana'
      : String(bdayInfo?.daysUntil ?? '');

  // Tenure / Antigüedad derivation
  const antiguedad = formatClientTenure(client.createdAt) || 'un tiempo';

  return {
    saludo,
    cliente,
    nombre: cliente,
    primer_nombre: primerNombre,
    nombre_completo: nombreCompleto,
    apellido,
    negocio,
    profesional,
    estilista: profesional,
    dias,
    dias_ausente: dias,
    citas,
    total_citas: citas,
    consumo,
    consumo_bs: consumoBs,
    total_ventas: consumo,
    total_ventas_bs: consumoBs,
    deuda,
    deuda_bs: deudaBs,
    saldo_deudor_bs: deudaBs,
    monto_deuda: deudaNum.toFixed(2),
    cumpleanos,
    dias_cumpleanos: diasCumpleanos,
    antiguedad,
    tiempo_cliente: antiguedad,
    ultima_cita: ultimaCita,
    ultimo_servicio: ultimoServicio,
    servicio: ultimoServicio,
    ultima_sede: ultimaSede,
    sede: ultimaSede,
    firma,
    cierre: firma,
    extra: '',
  };
}

export { interpolateTemplate } from './templateEngine.ts';

/**
 * Resolve the template string to use for a CRM client and active segment.
 */
export function resolveCrmTemplate(
  client: CrmClient,
  activeSegmentId: string,
  settings: Settings,
  segmentObj?: CrmSegment
): string {
  // 1. Debt priority if client owes money (except Birthday rules)
  const category = inferTemplateCategoryFromRules(segmentObj?.rules, segmentObj?.name, activeSegmentId);
  if ((client.outstandingAmount || 0) > 0.01 && category !== 'birthday') {
    return settings.msgTemplateDebtor || settings.templateCampaignDebtor;
  }

  // 2. Custom User Override for this specific segment ID
  if (activeSegmentId && settings.crmSegmentTemplates?.[activeSegmentId]) {
    return settings.crmSegmentTemplates[activeSegmentId];
  }

  // 3. Rule-Inferred Default Templates
  switch (category) {
    case 'birthday':
      return settings.msgTemplateBirthday;
    case 'top_spender':
      return settings.msgTemplateTopSpenders;
    case 'vip':
      return settings.msgTemplateVip || settings.templateCampaignVip;
    case 'lapsed':
      return settings.msgTemplateLapsed || settings.templateCampaignLapsed;
    case 'new':
    case 'first_visit':
      return settings.msgTemplateNewClients;
    case 'default':
    default:
      return settings.templateCrmDefault;
  }
}

/**
 * Generate intelligent WhatsApp message tailored for the client and active CRM segment.
 */
export function messageForCrmClient(
  client: CrmClient,
  activeSegmentId: string,
  settings: Settings,
  segmentObj?: CrmSegment
): string {
  const template = resolveCrmTemplate(client, activeSegmentId, settings, segmentObj);
  const tags = buildCrmTags(client, settings);
  return interpolateTemplate(template, tags);
}
